// src/pages/BotConnectionPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import BotSelection from '../components/BotConnection/BotSelection';
import ConfigurationPanel from '../components/BotConnection/ConfigurationPanel';
// ConsolePanel is likely deprecated/unused now ConsoleDisplay is used
// import ConsolePanel from '../components/BotConnection/ConsolePanel';
import OpCommandCenter from '../components/BotConnection/OpCommandCenter';
import StrategyTips from '../components/BotConnection/StrategyTips';
import PremiumFeatures from '../components/BotConnection/PremiumFeatures';
import BotDeploymentHistory from '../components/BotConnection/BotDeploymentHistory';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import PageTransition from '../components/Shared/PageTransition';
import { AnimatePresence, motion } from 'framer-motion';
import BotConnectionHeader from '../components/BotConnection/BotConnectionHeader';
import ConsoleDisplay from '../components/BotConnection/ConsoleDisplay'; // Use this component

import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { WS_BOT_CONNECTION_TYPES, WS_BOT_CONNECTION_TOPICS, getBotUserQueueTopic, getBotAppEndpoint } from '../config/wsBotConnectionConfig';
// Import shared server info config
import { WS_SERVER_INFO_TYPES, WS_SERVER_INFO_TOPICS, getServerInfoAppEndpoint, getServerInfoUserQueueTopic } from '../config/wsServerInfoConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import ProcessStatusDisplay from '../components/Shared/ProcessStatusDisplay';
import { handleWebSocketError } from '../utils/websocketErrorHandler';
import { useNotifications } from '../contexts/NotificationContext';

// --- Mock Data ---
const botTypesData = [
    { id: 'op-bot', name: 'OP Bot', description: 'Attempts to exploit vulnerabilities...', icon: 'user-shield', accentColor: 'purple', successRate: 32 },
    { id: 'crash-bot', name: 'Crash Bot', description: 'Attempts to crash the server...', icon: 'bolt', accentColor: 'orange', successRate: 78 },
    { id: 'account-bot', name: 'Account Bot', description: 'Attempts to find credentials...', icon: 'key', accentColor: 'yellow', successRate: 58 }
];
const historyData = [
    // Keep mock history for now
    { id: 1, date: 'June 15, 2023 14:32', server: 'mc.server123.net', botType: 'op-bot', result: 'Success', resultColorClass: 'text-accent-green bg-accent-green bg-opacity-20' },
    { id: 2, date: 'June 14, 2023 09:17', server: 'play.mineville.org', botType: 'crash-bot', result: 'Partial', resultColorClass: 'text-accent-orange bg-accent-orange bg-opacity-20' },
    { id: 3, date: 'June 12, 2023 22:05', server: 'play.hypixel.net', botType: 'account-bot', result: 'Failed', resultColorClass: 'text-accent-red bg-accent-red bg-opacity-20' }
];
// --- ---

const BotConnectionPage = ({openModal}) => {
    // --- Hooks ---
    const { isAuthenticated, user, isLoading: isAuthLoading } = useAuth();
    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const { serverInfo, isConnecting } = useServerConnection();
    const [deploymentHistory, setDeploymentHistory] = useState([]); // State for history
    const [isHistoryLoading, setIsHistoryLoading] = useState(false); // Loading state for history
    const [historyError, setHistoryError] = useState(null); // Error state for history
const {addNotification} = useNotifications();
 
    // --- State ---
    const [selectedBotType, setSelectedBotType] = useState(botTypesData[0].id);
    const [configuration, setConfiguration] = useState({
        // Default config matching ConfigurationPanel
        botUsername: 'random', // Will be 'MHF_Steve' for avatar if random
        attackMethod: 'console-exploit',
        botSkin: 'MHF_Steve', // Default skin for avatar
        botBehavior: 'stealth',
        numAttempts: 100,
    });
    const [isDeploying, setIsDeploying] = useState(false); // Tracks if a bot deployment is active
    const [deploymentStatus, setDeploymentStatus] = useState({ // Status specific to deployment process
        status: 'Idle',
        message: 'Connect to a server first.',
        color: 'text-gray-400',
    });
    const [consoleLines, setConsoleLines] = useState([]); // Lines for the ConsoleDisplay
    const [opAchieved, setOpAchieved] = useState(false); // Specific state for OP Bot success
    const [deploymentProgress, setDeploymentProgress] = useState({ currentAttempt: 0, commandsSent: 0 }); // For stats display
    const subscriptions = useRef({ // Store unsubscribe functions
        botHistory: null,
        botStatus: null,
        botProgress: null,
        botError: null
    });
    const requestedBotStateRef = useRef(false); // Track if bot state was requested
    const historyRequestedRef = useRef(false); // Prevent multiple history requests

    // --- Utility Functions ---
    const addConsoleLine = useCallback((text, type = 'info') => {
        setConsoleLines(prev => {
            const newLine = { type, text, timestamp: new Date().toISOString() };
            // Limit console lines to prevent performance issues
            const limitedLines = [...prev, newLine].slice(-200);
            return limitedLines;
        });
    }, []);
 
    const updateDeploymentStatus = useCallback((status, message, color) => {
        setDeploymentStatus({ status, message, color });
    }, []);

    const resetDeploymentProgress = useCallback(() => {
        setDeploymentProgress({ currentAttempt: 0, commandsSent: 0 });
    }, []);

    // --- Action Handlers ---
    const handleResetView = useCallback(() => {
        console.log("Resetting Bot Connection view.");
        setIsDeploying(false);
setOpAchieved(false);
        setDeploymentStatus({ status: 'Idle', message: 'Connect to a server and select a bot.', color: 'text-gray-400' });
        setConsoleLines([]); // Clear console on disconnect/reset
        resetDeploymentProgress(); // Reset progress stats
        requestedBotStateRef.current = false; // Allow bot state request again on next connection

        // Optional: Send disconnect message to backend if needed (e.g., for bots)
        // if (serverInfo?.ip && isWebSocketConnected) {
        //     sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/disconnect', { ip: serverInfo.ip });
        // }

    }, [isWebSocketConnected, sendMessage, resetDeploymentProgress]); // Removed isAuthLoading dependency as it's handled elsewhere

    // --- WebSocket Message Handlers ---

    // Handles BOT-SPECIFIC status updates (deployment started/completed, state restoration)
    const handleBotStatusMessage = useCallback((data, wsError) => {
        console.log("WS Bot Status Received:", data, wsError);

        // Handle generic BOT errors first if they come on this topic
        if (wsError || data?.type === WS_BOT_CONNECTION_TYPES.ERROR) {
            const errorMsg = data?.message || wsError?.message || "An unknown bot operation error occurred.";
            setIsDeploying(false); // Stop deploying state on error
            updateDeploymentStatus('Error', errorMsg, 'text-accent-red');
            addConsoleLine(`Bot Error: ${errorMsg}`, 'error');
            if(data?.isFatal) { // If backend indicates fatal, disconnect
                 handleResetView();
            }
            return;
        }

        // Process specific bot status types
        switch (data?.type) {
            case WS_BOT_CONNECTION_TYPES.DEPLOYMENT_STARTED:
                setIsDeploying(true);
                setOpAchieved(false); // Reset OP status on new deployment
                updateDeploymentStatus('Deploying', data.message || `Deploying ${data.botType}...`, 'text-accent-orange');
                addConsoleLine(data.message || `Deployment started for ${data.botType}`, 'system');
                break;

            case WS_BOT_CONNECTION_TYPES.DEPLOYMENT_COMPLETED:
                 setIsDeploying(false);
                 const success = data.success ?? false;
                 const message = data.message || (success ? 'Deployment completed successfully.' : 'Deployment failed or stopped.');
                 updateDeploymentStatus(
                     success ? 'Success' : 'Failed',
                     message,
                     success ? 'text-accent-green' : 'text-accent-red'
                 );
                 addConsoleLine(message, success ? 'success' : 'error');
                 // Check for OP achievement specifically
                 if (success && selectedBotType === 'op-bot' && data.resultData?.opAchieved) {
                     setOpAchieved(true);
                     addConsoleLine('Operator privileges confirmed!', 'success');
                     updateDeploymentStatus('Success', 'Operator privileges gained!', 'text-accent-green');
                 }
                 // Check for Account Bot success
                 if (success && selectedBotType === 'account-bot' && data.resultData?.password) {
                    addConsoleLine(`Account password found: ${data.resultData.password}`, 'success');
                    updateDeploymentStatus('Success', 'Account password found!', 'text-accent-green');
                 }
                break;

            case WS_BOT_CONNECTION_TYPES.CURRENT_STATE:
                console.log("Received Bot Current State:", data);
                // Only process if we are already connected via ServerInfo
                if (serverInfo) {
                    if (data.isActive && data.deployment) {
                        // Restore state of an ongoing deployment
                        setSelectedBotType(data.deployment.botType);
                        setIsDeploying(true); // Mark as deploying
                        // Restore configuration if backend provides it
                        if (data.deployment.config) {
                           setConfiguration(prev => ({ ...prev, ...data.deployment.config }));
                        }
                        updateDeploymentStatus('Deploying', data.deployment.status || 'Resumed deployment...', 'text-accent-orange');
                        // Set console lines ONLY if backend sends them, otherwise keep existing
                        if (data.deployment.logs) {
                            setConsoleLines(data.deployment.logs);
                        }
                        addConsoleLine('Resumed active deployment session.', 'system');
                        // Restore OP status if applicable
                        if (data.deployment.botType === 'op-bot' && data.deployment.resultData?.opAchieved) {
                            setOpAchieved(true);
                            addConsoleLine('Operator privileges were gained previously.', 'success');
                        }
                        // Restore Account bot found password if applicable
                        if (data.deployment.botType === 'account-bot' && data.deployment.resultData?.password) {
                             addConsoleLine(`Password was found previously: ${data.deployment.resultData.password}`, 'success');
                        }
                    } else if (data.isActive && !data.deployment) {
                        // Connected but no deployment running (should match state after handleServerInfoMessage)
                         updateDeploymentStatus('Connected', `Connected to ${serverInfo.hostname || serverInfo.ip}. Ready to deploy.`, 'text-accent-green');
                    } else {
                        // Backend says no active bot state, ensure UI reflects this
                        setIsDeploying(false);
                        updateDeploymentStatus('Connected', `Connected to ${serverInfo.hostname || serverInfo.ip}. Ready to deploy.`, 'text-accent-green');
                    }
                } else {
                     console.warn("Received bot state but not connected via ServerInfo. Ignoring.");
                }
                break;

            // NOTE: CONNECTION_STATUS type is now handled by handleServerInfoMessage
            // case WS_BOT_CONNECTION_TYPES.CONNECTION_STATUS:
            //    break;

            default:
                console.warn("Unhandled bot status message type:", data?.type);
        }
    }, [addConsoleLine, updateDeploymentStatus, selectedBotType, serverInfo, handleResetView]);

    const handleSendCommand = useCallback((command) => {
        // TODO: Implement logic to send bot-specific commands if needed
        console.log("Bot Console Command Received (Not Implemented):", command);
        addConsoleLine(`> ${command}`, 'command');
        addConsoleLine(`Command handling not implemented for bots yet.`, 'warning');
     }, [addConsoleLine]); // Add dependencies if needed

// Handles BOT-SPECIFIC progress updates (console lines)
    const handleBotProgressMessage = useCallback((message, wsError) => {
        const type = message?.type;
        const data = message?.data;
        // console.log("WS Bot Progress Received:", data, wsError); // Can be noisy
        if (wsError) {
            addConsoleLine(`Progress Error: ${wsError.message}`, 'error');
            return;
        }
        // Check specific types expected on the progress topic
        if (type === WS_BOT_CONNECTION_TYPES.DEPLOYMENT_PROGRESS && data) {
            if(data.line) {
                addConsoleLine(data.line.text, data.line.type || 'info');
            }
            // Update header status only if provided in the progress message
            if (data.status) {
                updateDeploymentStatus('Deploying', data.status, 'text-accent-orange');
            }
            // New: Update progress stats
            if (data.progressStats) { // Assuming backend sends { currentAttempt: X, commandsSent: Y }
                setDeploymentProgress(prev => ({ ...prev, ...data.progressStats }));
            }
        } else if (type === WS_BOT_CONNECTION_TYPES.CONSOLE_LINE && data.line) { // Handle generic console lines if sent separately
            addConsoleLine(data.line.text, data.line.type || 'info');
        } else {
             console.warn("Unhandled bot progress message:", message);
        }
    }, [addConsoleLine, updateDeploymentStatus]);

    // Handles BOT-SPECIFIC errors
    const handleBotErrorMessage = useCallback((data, wsError) => {
        const errorMsg = handleWebSocketError(
            data,
            wsError,
            openModal,
            addNotification,
            {
              defaultTitle: 'RCON Error',
              moduleName: 'RCON Hacking' // Имя модуля для логов
            }
          );
         // Don't necessarily set connectionError here, keep it for initial connect errors
         setIsDeploying(false); // Stop deploying state
         updateDeploymentStatus('Error', errorMsg, 'text-accent-red');
         addConsoleLine(`Error: ${errorMsg}`, 'error');
         if (data?.isFatal) {
             // Maybe reset fully? Or just stop deployment?
             handleResetView(); // Reset if fatal bot error
         }
    }, [addConsoleLine, updateDeploymentStatus, handleResetView]);

    // --- NEW: History Handler ---
    const handleHistoryMessage = useCallback((message, wsError) => {
        setIsHistoryLoading(false); // Stop loading
        if (wsError || message?.type === WS_BOT_CONNECTION_TYPES.HISTORY_ERROR) {
            const errorMsg = message?.error || wsError?.message || "Failed to load deployment history.";
            console.error("Bot History WS Error:", errorMsg);
            setHistoryError(errorMsg);
            setDeploymentHistory([]);
        } else if (message?.type === WS_BOT_CONNECTION_TYPES.HISTORY_LIST && message.data?.history) {
            console.log("Received Bot History:", message.data.history);
            setDeploymentHistory(message.data.history); // Update state
            setHistoryError(null);
        } else {
            console.warn("Received unknown/invalid bot history message:", message);
            setHistoryError("Received unexpected history data format.");
            setDeploymentHistory([]);
        }
    }, []); // No dependencies needed

    // --- WebSocket Subscription Effect ---
    useEffect(() => {
        // Wait for auth check to complete
        if (isAuthLoading) {
            console.log("BotConnection WS Effect: Auth loading, skipping setup.");
            updateDeploymentStatus('Initializing', 'Waiting for authentication...', 'text-gray-400');
            return;
        }

        // Reset status if auth is done but not connected/authenticated
        if (!isWebSocketConnected || !isAuthenticated) {
            updateDeploymentStatus('Disconnected', 'Please log in and ensure WebSocket connection.', 'text-gray-500');
            handleResetView(); // Reset view if not logged in
        }

        // --- Cleanup function ---
        const cleanupAllSubscriptions = () => {
            console.log("Cleaning up ALL BotConnection subscriptions.");
            Object.values(subscriptions.current).forEach(unsub => unsub?.());
            subscriptions.current = { serverInfo: null, serverError: null, botStatus: null, botProgress: null, botError: null, botHistory: null };
        };

        // Only proceed if WebSocket is connected AND user is authenticated
        if (isWebSocketConnected && isAuthenticated) {
            console.log("Setting up WS subscriptions (BotConnectionPage)...");
            cleanupAllSubscriptions();

            // --- Conditionally Subscribe to Bot Topics (only after connection) ---
            if (serverInfo) { // Check if we have successfully connected
                const botStatusTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.STATUS;
                const botProgressTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.PROGRESS;
                const botErrorTopic = WS_BOT_CONNECTION_TOPICS.USER_QUEUE_BASE + WS_BOT_CONNECTION_TOPICS.ERROR;

                // Ensure previous bot subs are cleaned up
                if (subscriptions.current.botStatus) subscriptions.current.botStatus();
                if (subscriptions.current.botProgress) subscriptions.current.botProgress();
                if (subscriptions.current.botError) subscriptions.current.botError();

                subscriptions.current.botStatus = subscribe(botStatusTopic, handleBotStatusMessage);
                subscriptions.current.botProgress = subscribe(botProgressTopic, handleBotProgressMessage);
                subscriptions.current.botError = subscribe(botErrorTopic, handleBotErrorMessage);
            } else {
                 // If not connected (serverInfo is null), ensure bot subs are cleaned
                 if (subscriptions.current.botStatus) subscriptions.current.botStatus();
                 if (subscriptions.current.botProgress) subscriptions.current.botProgress();
                 if (subscriptions.current.botError) subscriptions.current.botError();
                 subscriptions.current.botStatus = null;
                 subscriptions.current.botProgress = null;
                 subscriptions.current.botError = null;
            }
            // Subscribe to History topic ALWAYS when connected/authed
            subscriptions.current.botHistory = subscribe(getBotUserQueueTopic(WS_BOT_CONNECTION_TOPICS.USER_HISTORY), handleHistoryMessage); // Use history topic

            // Request history ONLY ONCE per connection/auth session
             if (!historyRequestedRef.current) {
                 const historyTimerId = setTimeout(() => {
                     if (isWebSocketConnected && isAuthenticated) { // Check again
                         console.log("Requesting Bot Deployment History...");
                         setIsHistoryLoading(true);
                         setHistoryError(null);
                         sendMessage(getBotAppEndpoint(WS_BOT_CONNECTION_TOPICS.APP_GET_HISTORY), {}); // Use new app endpoint
                         historyRequestedRef.current = true; // Mark as requested
                     }
                 }, 200);
                  return () => { clearTimeout(historyTimerId); }; // Clear timeout on cleanup
             }

            // Return the master cleanup function
            return () => {
                cleanupAllSubscriptions();
            }
        } else {
            // Cleanup if WS disconnects or user logs out
            cleanupAllSubscriptions();
            requestedBotStateRef.current = false; // Reset flag
        }

    // Dependencies: These trigger re-subscription/cleanup
    }, [
        isWebSocketConnected, isAuthenticated, isAuthLoading, serverInfo, // serverInfo added to trigger bot subs
        subscribe, sendMessage, // Core WS functions
        // Handler functions (stable due to useCallback)
                // Handler functions (stable due to useCallback)
        serverInfo, handleBotStatusMessage, handleBotProgressMessage, handleBotErrorMessage,
        updateDeploymentStatus, handleResetView, handleHistoryMessage, resetDeploymentProgress
    ]);
    // --- Other Action Handlers (handleDeployBot, handleStopDeployment, handleBotTypeChange, handleConfigurationChange) ---
    // These remain largely the same as they interact with the BOT endpoints after connection is established
    const handleDeployBot = useCallback(() => {
        if (isAuthLoading || !isAuthenticated || !isWebSocketConnected || !serverInfo || isDeploying || isConnecting) {
           addConsoleLine('Cannot deploy: Not ready or already deploying.', 'error');
           console.warn("Deploy cancelled:", { isAuthLoading, isAuthenticated, isWebSocketConnected, serverInfo, isDeploying, isConnecting });
           return;
       }
       const botToDeploy = botTypesData.find(b => b.id === selectedBotType);
       if (!botToDeploy) {
           addConsoleLine('Invalid bot type selected.', 'error');
           return;
       }

       const botNameDisplay = botToDeploy.name;
       console.log(`Requesting deployment of ${botNameDisplay} to ${serverInfo.ip} with config:`, configuration);
        addConsoleLine(`Initiating deployment of ${botNameDisplay}...`, 'system');
        updateDeploymentStatus('Starting', `Sending deployment request for ${botNameDisplay}...`, 'text-accent-orange');
        setIsDeploying(true); // Set deploying state immediately
        resetDeploymentProgress(); // Reset progress for new deployment
 
        sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/deploy', {
           ip: serverInfo.ip, // Use the confirmed server IP
           botType: selectedBotType,
           config: configuration, // Send the full config object
       });
    }, [
        serverInfo, isDeploying, isConnecting, isWebSocketConnected, isAuthenticated, isAuthLoading, selectedBotType, configuration,
        sendMessage, addConsoleLine, updateDeploymentStatus, resetDeploymentProgress
     ]);

    const handleStopDeployment = useCallback(() => {
       if (isAuthLoading || !serverInfo || !isDeploying || !isWebSocketConnected) {
          addConsoleLine('No active deployment to stop.', 'warning');
          console.warn("Stop cancelled:", { isAuthLoading, serverInfo, isDeploying, isWebSocketConnected });
          return;
       }
       console.log("Requesting to stop deployment...");
       addConsoleLine("Attempting to stop deployment...", 'system');
       updateDeploymentStatus('Stopping', 'Sending stop request...', 'text-accent-red');
       // Keep isDeploying true until confirmation comes back? Or set false optimistically?
       // Let's wait for backend confirmation (DEPLOYMENT_COMPLETED with success: false or specific stop message)
       // Keep isDeploying true until confirmation comes back? Or set false optimistically?
        // Let's wait for backend confirmation (DEPLOYMENT_COMPLETED with success: false or specific stop message)
        sendMessage(WS_BOT_CONNECTION_TOPICS.APP_ENDPOINT + '/stop', { ip: serverInfo.ip });
    }, [serverInfo, isDeploying, isWebSocketConnected, isAuthLoading, sendMessage, addConsoleLine, updateDeploymentStatus]);

    const handleBotTypeChange = useCallback((botType) => {
       setSelectedBotType(botType);
       if (serverInfo && !isDeploying) {
           const botName = botTypesData.find(b => b.id === botType)?.name || botType;
           addConsoleLine(`Selected bot type: ${botName}`, 'info');
       }
    }, [serverInfo, isDeploying, addConsoleLine]);

    const handleConfigurationChange = useCallback((newConfig) => {
       setConfiguration(prev => ({ ...prev, ...newConfig }));
    }, []);

    // --- Derived State ---
    const selectedBot = botTypesData.find(bot => bot.id === selectedBotType);
    const isPageLoading = isAuthLoading;
    const isDeploymentDisabled = !serverInfo || isConnecting || isPageLoading || isDeploying;
    const isBotConsoleInputEnabled = opAchieved && !isDeploying; //

    // --- Render ---
    return (
        <PageTransition>
            <div className="p-6 lg:p-8 relative">

                {/* Auth Loading / Disconnected Overlay */}
                {/*(isPageLoading || (!isAuthenticated && !isAuthLoading)) && (
                    <div className="absolute inset-0 bg-dark-800 bg-opacity-70 flex items-center justify-center z-50 rounded-lg">
                        <div className="text-center p-6 bg-dark-700 rounded-lg shadow-lg">
                            <i className={`fas ${isPageLoading ? 'fa-spinner fa-spin' : 'fa-exclamation-triangle'} text-accent-purple text-4xl mb-4`}></i>
                            <p className="text-gray-300">
                                {isPageLoading ? 'Initializing module...' : 'Please log in to use this feature.'}
                            </p>
                        </div>
                    </div>
                )*/}

                <BotConnectionHeader />
                <ServerInfoCard />

                {/* Bot Selection - Disabled if cannot deploy */}
                <div className={`transition-opacity duration-300 ${isDeploymentDisabled ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
                    <BotSelection
                        botTypes={botTypesData}
                        selectedBotType={selectedBotType}
                        onSelectBotType={handleBotTypeChange}
                    />
                </div>

                <AnimatePresence>
                    {/* Render config/console only when connected */}
                    {!isPageLoading && (
                        <motion.div
                            key="config-console" // Add key for AnimatePresence
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8"
                        >
                            <ConfigurationPanel
                                selectedBot={selectedBot}
                                configuration={configuration}
                                onConfigurationChange={handleConfigurationChange}
                                onDeploy={handleDeployBot}
                                isDeploying={isDeploying}
                                isDisabled={isDeploymentDisabled} // Use combined disabled state
                            />
                            {/* THIS IS THE NEW BOT CONSOLE STRUCTURE replacing the direct ProcessStatusDisplay */}
                            <div className="lg:col-span-2">
                                <div className="bg-dark-600 rounded-2xl p-6 h-full flex flex-col relative">
                                    {/* Header: Title "Bot Console" and Status */}
                                    <div className="flex justify-between items-center mb-5">
                                        <h3 className="font-bold text-lg flex items-center">
                                            <i className={`fas fa-terminal mr-2 text-accent-${selectedBot?.accentColor || 'purple'}`}></i>
                                            {selectedBot?.name || 'Bot'} Console
                                        </h3>
                                        <div className="flex items-center">
                                            <span className={`text-xs py-1 px-2 bg-dark-700 rounded-lg ${deploymentStatus.color}`}>
                                                Status: {deploymentStatus.status}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Bot visualization */}
                                    <div className="mb-6 flex justify-center">
                                        <div className={`relative bot-float ${isDeploying ? 'animate-pulse-slow' : ''}`}>
                                            <div className={`w-20 h-20 bg-dark-500 rounded-xl p-1 border border-accent-${selectedBot?.accentColor || 'purple'} overflow-hidden`}>
                                                <img
                                                    src={`https://mc-heads.net/avatar/${configuration.botSkin || 'MHF_Steve'}/100`}
                                                    alt={`${configuration.botSkin || 'Bot'} character`}
                                                    className="w-full h-auto minecraft-head"
                                                />
                                            </div>
                                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-4 bg-dark-400 rounded-full opacity-30"></div>
                                        </div>
                                    </div>

                                    {/* Terminal Output (using ProcessStatusDisplay in minimal mode) */}
                                    <ProcessStatusDisplay
                                        lines={consoleLines}
                                        isConsoleInputEnabled={isBotConsoleInputEnabled}
                                        onSendCommand={handleSendCommand}
                                        consoleMaxHeight="h-[200px]" // Adjusted height for this layout
                                        showHeader={false}
                                        showProgressBar={false}
                                        showActions={false}
                                        showMainBorder={false}
                                        className="mb-4 flex-1" // flex-1 to grow, mb-4 for spacing before stats
                                    />

                                    {/* Bot Statistics */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                        <div className="bg-dark-700 rounded-lg p-3">
                                            <div className="text-xs text-gray-400 mb-1">Attempts</div>
                                            <div className="font-bold text-lg">
                                                {deploymentProgress.currentAttempt || 0}/{configuration.numAttempts || 'N/A'}
                                            </div>
                                        </div>
                                        <div className="bg-dark-700 rounded-lg p-3">
                                            <div className="text-xs text-gray-400 mb-1">Commands</div>
                                            <div className="font-bold text-lg">{deploymentProgress.commandsSent || 0}</div>
                                        </div>
                                        <div className="bg-dark-700 rounded-lg p-3">
                                            <div className="text-xs text-gray-400 mb-1">Connection</div>
                                            <div className={`font-bold text-lg ${isDeploying || (serverInfo && !isConnecting) ? 'text-accent-green' : 'text-gray-400'}`}>
                                                {isDeploying ? 'Active' : (serverInfo && !isConnecting ? 'Ready' : 'Waiting')}
                                            </div>
                                        </div>
                                        <div className="bg-dark-700 rounded-lg p-3">
                                            <div className="text-xs text-gray-400 mb-1">OP Status</div>
                                            <div className={`font-bold text-lg ${opAchieved ? 'text-accent-green' : (isDeploying ? 'text-accent-orange' : 'text-gray-400')}`}>
                                                {opAchieved ? 'Achieved!' : (isDeploying ? 'Trying...' : 'Waiting')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-wrap sm:flex-nowrap gap-3">
                                        <button
                                          onClick={() => console.log("Save Config clicked - Placeholder")} // TODO: Implement save config
                                          disabled={isDeploying}
                                          className="px-4 py-2.5 bg-dark-500 hover:bg-dark-400 rounded-xl text-sm font-medium transition flex-1 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <i className="fas fa-save mr-2"></i> Save Config
                                        </button>
                                        <button
                                          onClick={handleStopDeployment}
                                          disabled={!isDeploying}
                                          className={`px-4 py-2.5 rounded-xl text-sm font-medium transition flex-1 flex items-center justify-center ${
                                              isDeploying ? 'bg-accent-red hover:bg-opacity-90 text-white' : 'bg-dark-500 text-gray-400 opacity-50 cursor-not-allowed'
                                          }`}
                                        >
                                            <i className="fas fa-stop mr-2"></i> Stop Bot
                                        </button>
                                        <button
                                          onClick={() => {
                                            const textToCopy = consoleLines.map(l => `[${l.type}] ${l.text}`).join('\n');
                                            navigator.clipboard.writeText(textToCopy).then(() => {
                                                addNotification({title: "Console Log", message: "Copied to clipboard!", icon: "fa-clipboard", color: "accent-green"});
                                            }).catch(err => {
                                                addNotification({title: "Error", message: "Failed to copy log.", icon: "fa-exclamation-triangle", color: "accent-red"});
                                            });
                                          }}
                                          disabled={consoleLines.length === 0 || isDeploying}
                                          className="px-4 py-2.5 bg-dark-500 hover:bg-dark-400 rounded-xl text-sm font-medium transition flex-1 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <i className="fas fa-clipboard mr-2"></i> Copy Results
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* OP Command Center - Disabled if cannot deploy OR OP not achieved */}
                <div className={`transition-opacity duration-300 ${isDeploymentDisabled || !opAchieved ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
                    <OpCommandCenter isDisabled={isDeploymentDisabled || !opAchieved} />
                </div>

                {/* Static sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <StrategyTips />
                    <PremiumFeatures />
                </div>
                <BotDeploymentHistory
                    history={deploymentHistory}
                    isLoading={isHistoryLoading}
                    error={historyError}
                 />

            </div>
        </PageTransition>
    );
};

export default BotConnectionPage;